/**
 * Check-in — the consent checkbox, the browser-geolocation button, and the result,
 * WP-W10. `web/shapes/geo.md` §4 is the agreed shape.
 *
 * This component owns no state and makes no request: `GeoScreen` drives
 * `navigator.geolocation` and the mutation, because both need to be mocked (the
 * former) or driven over a stubbed `fetch` (the latter) from the same test, and a
 * presentational component is what keeps that test from having to reach inside a
 * hook. What this file owns is the one rule `web/shapes/geo.md` §2 states: the
 * button is **disabled until the consent checkbox is checked**, so the browser's own
 * permission prompt can never appear for a check-in nobody has agreed to yet.
 *
 * ── The two named `409 WORKFORCE_CONFLICT` reasons get their own sentence ────
 *
 * `geoCheckinConflictReason` reads `recordGeoCheckin`'s refusal back into
 * `'geo_checkin_disabled'` or `'geo_consent_required'`. Both are on
 * `error-safety.test.ts`'s user-safe list, so `ApiErrorNote` would already print the
 * server's own sentence for either — but "the company has not turned this on" and
 * "you have not consented" call for two different next actions (ask an admin, versus
 * check the box above and try again), and a generic conflict note cannot tell them
 * apart. So a recognised reason renders its own `StatusNote` instead of the generic
 * `ApiErrorNote`; anything else — a transport failure, a 5xx, an unrecognised
 * `details.reason` — still falls through to `ApiErrorNote` unchanged.
 *
 * ── The `block`-mode refusal gets its own sentence too (Task T2-C-web) ────────
 *
 * `400 WORKFORCE_GEO_OUTSIDE_GEOFENCE` is what `outside_geofence_mode: 'block'`
 * raises instead of storing the check-in. An employee cannot read the company's
 * policy (`workforce.manage_policies`-gated) to learn that mode is even on, so this
 * refusal's own body is the only place they can learn how far they are from the
 * geofence. `geoCheckinOutsideGeofenceDetail` reads `distance_m`/`radius_m` back off
 * it; when both are present this renders its own `StatusNote` with the distance
 * sentence, the same "a named refusal gets its own words" treatment the two
 * `409 WORKFORCE_CONFLICT` reasons above already get.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { GeoCheckin } from '../../api/geo';
import {
  geoCheckinConflictReason,
  geoCheckinOutsideGeofenceDetail,
  type GeolocationFailureKind,
} from '../../geo/geo';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { SEVERITY_ICON } from '../../states/icons';
import { StatusNote } from '../../states/StatusNote';

export interface CheckinPanelProps {
  readonly consentAck: boolean;
  readonly onConsentChange: (checked: boolean) => void;
  readonly locating: boolean;
  readonly geoFailure: GeolocationFailureKind | null;
  readonly onCheckIn: () => void;
  readonly submitPending: boolean;
  readonly submitError: WorkforceApiError | null;
  readonly result: GeoCheckin | null;
}

export function CheckinPanel({
  consentAck,
  onConsentChange,
  locating,
  geoFailure,
  onCheckIn,
  submitPending,
  submitError,
  result,
}: CheckinPanelProps): ReactNode {
  const { t } = useI18n();
  const working = locating || submitPending;
  const conflictReason = submitError === null ? null : geoCheckinConflictReason(submitError);
  const outsideGeofence =
    submitError === null ? null : geoCheckinOutsideGeofenceDetail(submitError);

  return (
    <section className="wp-section" aria-labelledby="w10-checkin-heading" data-testid="w10-checkin">
      <h2 className="wp-section__title" id="w10-checkin-heading">
        {t('geo.checkin.title')}
      </h2>
      <p className="wp-small wp-muted">{t('geo.checkin.intro')}</p>

      <div className="wp-filters__group">
        <label className="wp-toggle" htmlFor="w10-checkin-consent">
          <input
            id="w10-checkin-consent"
            type="checkbox"
            data-testid="w10-checkin-consent"
            checked={consentAck}
            onChange={(event) => {
              onConsentChange(event.target.checked);
            }}
          />
          <span>{t('geo.checkin.consent')}</span>
        </label>
      </div>

      <div className="wp-filters__actions">
        <button
          className="wp-button wp-button--primary"
          type="button"
          data-testid="w10-checkin-button"
          disabled={!consentAck || working}
          onClick={onCheckIn}
        >
          {locating
            ? t('geo.checkin.locating')
            : submitPending
              ? t('geo.checkin.submitting')
              : t('geo.checkin.button')}
        </button>
      </div>

      {geoFailure === null ? null : (
        <StatusNote
          severity="medium"
          role="alert"
          title={t(`geo.checkin.failure.${geoFailure}`)}
          testId="w10-checkin-geo-failure"
        />
      )}

      {submitError === null ? null : conflictReason !== null ? (
        <StatusNote
          severity="medium"
          role="alert"
          title={t(`geo.checkin.conflict.${conflictReason}`)}
          testId="w10-checkin-conflict"
        />
      ) : outsideGeofence !== null ? (
        <StatusNote
          severity="medium"
          role="alert"
          title={t('geo.checkin.outsideGeofence', {
            distance: Math.round(outsideGeofence.distanceM),
            radius: outsideGeofence.radiusM,
          })}
          testId="w10-checkin-outside-geofence"
        />
      ) : (
        <ApiErrorNote
          error={submitError}
          subject={t('geo.checkin.subject')}
          testId="w10-checkin-error"
        />
      )}

      {result === null ? null : (
        <div data-testid="w10-checkin-result">
          {/* Migration 055 — `within_geofence: null` means this employee has opted
              out of location capture: the check-in was stored, and no coordinates
              were. Told plainly on their OWN screen, because the one person who
              should never have to guess what was collected is the subject. */}
          {result.within_geofence === null ? (
            <>
              <Badge
                severity="info"
                icon={SEVERITY_ICON.info}
                label={t('geo.badge.noLocation')}
                testId="w10-checkin-result-badge"
              />
              <p className="wp-small wp-muted" data-testid="w10-checkin-result-detail">
                {t('geo.checkin.result.noLocation')}
              </p>
            </>
          ) : (
            <>
              <Badge
                severity={result.within_geofence ? 'low' : 'medium'}
                icon={SEVERITY_ICON[result.within_geofence ? 'low' : 'medium']}
                label={t(result.within_geofence ? 'geo.badge.within' : 'geo.badge.outside')}
                testId="w10-checkin-result-badge"
              />
              <p className="wp-small wp-muted" data-testid="w10-checkin-result-detail">
                {result.office === null || result.distance_m === null
                  ? t('geo.checkin.result.noOffice')
                  : t(
                      result.within_geofence
                        ? 'geo.checkin.result.within'
                        : 'geo.checkin.result.outside',
                      {
                        office: result.office.name,
                        distance: Math.round(result.distance_m),
                      },
                    )}
              </p>
            </>
          )}
        </div>
      )}
    </section>
  );
}
